<template>
  <div class="xss-panel">
    <!-- Header -->
    <div class="xss-header">
      <div class="xss-header-left">
        <span class="xss-icon">
          <ShieldCheck class="w-8 h-8 text-blue-600" />
        </span>
        <div>
          <h2 class="xss-title text-xl font-bold">DOM XSS Risk Scanner</h2>
          <p class="xss-subtitle text-sm">
            Detects inline event handlers, dangerous JS sinks, unsafe URIs,
            missing SRI, and DOM clobbering risks.
          </p>
        </div>
      </div>
      <div class="xss-badges" v-if="xssData && xssData.summary">
        <span class="badge badge-high text-xs font-bold"
          >{{ xssData.summary.high }} High</span
        >
        <span class="badge badge-medium text-xs font-bold"
          >{{ xssData.summary.medium }} Medium</span
        >
        <span class="badge badge-low text-xs font-bold"
          >{{ xssData.summary.low }} Low</span
        >
      </div>
    </div>

    <!-- No data yet -->
    <div v-if="!xssData" class="xss-empty">
      <div class="empty-icon flex justify-center">
        <Search class="w-10 h-10 text-gray-400" />
      </div>
      <p class="empty-title text-lg font-semibold">No XSS Scan Data</p>
      <p class="empty-desc text-sm">
        Scan a URL to check for DOM-based XSS risks.
      </p>
    </div>

    <!-- All clear -->
    <div
      v-else-if="xssData.issues && xssData.issues.length === 0"
      class="xss-clear"
    >
      <div class="clear-icon flex justify-center">
        <CheckCircle2 class="w-10 h-10 text-green-500" />
      </div>
      <p class="clear-title text-lg font-semibold">No XSS Risks Detected</p>
      <p class="clear-desc text-sm">
        No obvious DOM-based XSS risk patterns were found on this page.
      </p>
    </div>

    <!-- Results -->
    <div v-else>
      <!-- Filter Tabs -->
      <div class="filter-tabs">
        <button
          v-for="tab in filterTabs"
          :key="tab.id"
          class="filter-tab text-sm font-semibold"
          :class="{ active: activeFilter === tab.id }"
          @click="activeFilter = tab.id"
        >
          <span class="tab-dot" :class="`dot-${tab.id}`"></span>
          {{ tab.label }}
          <span class="tab-count text-xs">{{ tab.count }}</span>
        </button>
      </div>

      <!-- Issue Cards -->
      <div class="issues-list">
        <div
          v-for="(issue, index) in filteredIssues"
          :key="index"
          class="issue-card"
          :class="`issue-${issue.severity}`"
        >
          <!-- Card Header -->
          <div class="issue-card-header">
            <div class="issue-left">
              <span
                class="severity-badge text-xxs font-extrabold"
                :class="`sev-${issue.severity}`"
              >
                {{ issue.severity.toUpperCase() }}
              </span>
              <span class="issue-type text-base font-semibold">{{
                issue.type
              }}</span>
            </div>
            <button
              class="expand-btn text-xs flex items-center justify-center"
              @click="toggleExpand(index)"
              :aria-label="expandedIndex === index ? 'Collapse' : 'Expand'"
            >
              <ChevronUp v-if="expandedIndex === index" class="w-4 h-4" />
              <ChevronDown v-else class="w-4 h-4" />
            </button>
          </div>

          <!-- Snippet / Identifier -->
          <div
            class="issue-snippet"
            v-if="issue.snippet || issue.value || issue.src || issue.id"
          >
            <code class="font-mono text-xs">{{
              issue.snippet || issue.value || issue.src || `id="${issue.id}"`
            }}</code>
          </div>

          <!-- Expandable Details -->
          <transition name="expand">
            <div v-if="expandedIndex === index" class="issue-details">
              <!-- Meta row -->
              <div
                class="meta-row"
                v-if="
                  issue.attribute || issue.sink || issue.source || issue.tag
                "
              >
                <span
                  v-if="issue.attribute"
                  class="meta-chip chip-blue text-xs font-semibold font-mono"
                  >attr: {{ issue.attribute }}</span
                >
                <span
                  v-if="issue.sink"
                  class="meta-chip chip-red text-xs font-semibold font-mono"
                  >sink: {{ issue.sink }}</span
                >
                <span
                  v-if="issue.source"
                  class="meta-chip chip-orange text-xs font-semibold font-mono"
                  >source: {{ issue.source }}</span
                >
                <span
                  v-if="issue.tag"
                  class="meta-chip chip-gray text-xs font-semibold font-mono"
                  >tag: &lt;{{ issue.tag }}&gt;</span
                >
                <span
                  v-if="issue.line"
                  class="meta-chip chip-gray text-xs font-semibold font-mono"
                  >line: {{ issue.line }}</span
                >
                <span
                  v-if="issue.count"
                  class="meta-chip chip-orange text-xs font-semibold font-mono"
                  >occurrences: {{ issue.count }}</span
                >
              </div>

              <!-- Description -->
              <p class="issue-desc text-sm">
                <strong>Risk:</strong> {{ issue.description }}
              </p>

              <!-- Recommendation -->
              <div class="issue-recommendation">
                <span class="rec-icon flex items-center justify-center">
                  <Lightbulb class="w-4 h-4 text-amber-500 flex-shrink-0" />
                </span>
                <p class="text-sm">{{ issue.recommendation }}</p>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ShieldCheck,
  Search,
  CheckCircle2,
  Lightbulb,
  ChevronUp,
  ChevronDown,
} from "lucide-vue-next";

export default {
  name: "XssRiskPanel",
  components: {
    ShieldCheck,
    Search,
    CheckCircle2,
    Lightbulb,
    ChevronUp,
    ChevronDown,
  },
  props: {
    xssData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeFilter: "all",
      expandedIndex: null,
    };
  },
  computed: {
    filterTabs() {
      if (!this.xssData || !this.xssData.issues) return [];
      const issues = this.xssData.issues;
      return [
        { id: "all", label: "All", count: issues.length },
        {
          id: "high",
          label: "High",
          count: issues.filter((i) => i.severity === "high").length,
        },
        {
          id: "medium",
          label: "Medium",
          count: issues.filter((i) => i.severity === "medium").length,
        },
        {
          id: "low",
          label: "Low",
          count: issues.filter((i) => i.severity === "low").length,
        },
      ];
    },
    filteredIssues() {
      if (!this.xssData || !this.xssData.issues) return [];
      if (this.activeFilter === "all") return this.xssData.issues;
      return this.xssData.issues.filter(
        (i) => i.severity === this.activeFilter
      );
    },
  },
  methods: {
    toggleExpand(index) {
      this.expandedIndex = this.expandedIndex === index ? null : index;
    },
  },
};
</script>

<style scoped>
/* ── Panel Shell ───────────────────────────────────────────────────── */
.xss-panel {
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
}

/* ── Header ───────────────────────────────────────────────────────── */
.xss-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 12px;
}
.xss-header-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.xss-icon {
  line-height: 1;
}
.xss-title {
  color: #111827;
  margin: 0 0 4px;
}
.xss-subtitle {
  color: #6b7280;
  margin: 0;
  max-width: 500px;
}

/* ── Summary Badges ───────────────────────────────────────────────── */
.xss-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.badge {
  padding: 4px 12px;
  border-radius: 999px;
  letter-spacing: 0.03em;
}
.badge-high {
  background: #fee2e2;
  color: #991b1b;
}
.badge-medium {
  background: #fef3c7;
  color: #92400e;
}
.badge-low {
  background: #e0f2fe;
  color: #075985;
}

/* ── Empty / Clear States ─────────────────────────────────────────── */
.xss-empty,
.xss-clear {
  text-align: center;
  padding: 60px 20px;
  background: #f9fafb;
  border-radius: 10px;
  border: 2px dashed #e5e7eb;
}
.empty-icon,
.clear-icon {
  margin-bottom: 12px;
}
.empty-title,
.clear-title {
  color: #374151;
  margin: 0 0 6px;
}
.empty-desc,
.clear-desc {
  color: #9ca3af;
  margin: 0;
}

/* ── Filter Tabs ──────────────────────────────────────────────────── */
.filter-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.filter-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
}
.filter-tab:hover {
  border-color: #9ca3af;
  color: #374151;
}
.filter-tab.active {
  border-color: #6366f1;
  background: #eef2ff;
  color: #4338ca;
}
.tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.dot-all {
  background: #6b7280;
}
.dot-high {
  background: #ef4444;
}
.dot-medium {
  background: #f59e0b;
}
.dot-low {
  background: #3b82f6;
}
.tab-count {
  background: #e5e7eb;
  border-radius: 999px;
  padding: 1px 7px;
}
.filter-tab.active .tab-count {
  background: #c7d2fe;
}

/* ── Issue Cards ──────────────────────────────────────────────────── */
.issues-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.issue-card {
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  overflow: hidden;
  transition: box-shadow 0.2s;
}
.issue-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}
.issue-high {
  border-left: 4px solid #ef4444;
}
.issue-medium {
  border-left: 4px solid #f59e0b;
}
.issue-low {
  border-left: 4px solid #3b82f6;
}

.issue-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  cursor: pointer;
}
.issue-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.severity-badge {
  padding: 2px 9px;
  border-radius: 999px;
  letter-spacing: 0.06em;
}
.sev-high {
  background: #fee2e2;
  color: #b91c1c;
}
.sev-medium {
  background: #fef3c7;
  color: #b45309;
}
.sev-low {
  background: #dbeafe;
  color: #1d4ed8;
}

.issue-type {
  color: #1f2937;
}
.expand-btn {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: background 0.15s;
}
.expand-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.issue-snippet {
  padding: 8px 16px;
  background: #1e1e2e;
  overflow-x: auto;
}
.issue-snippet code {
  color: #cdd6f4;
  white-space: pre;
}

/* ── Issue Details (expanded) ─────────────────────────────────────── */
.issue-details {
  padding: 14px 16px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.meta-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.meta-chip {
  padding: 2px 10px;
  border-radius: 6px;
}
.chip-blue {
  background: #eff6ff;
  color: #1d4ed8;
}
.chip-red {
  background: #fff1f2;
  color: #be123c;
}
.chip-orange {
  background: #fffbeb;
  color: #b45309;
}
.chip-gray {
  background: #f3f4f6;
  color: #4b5563;
}

.issue-desc {
  color: #374151;
  margin: 0;
  line-height: 1.6;
}
.issue-recommendation {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 10px 14px;
}
.rec-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.issue-recommendation p {
  color: #166534;
  margin: 0;
  line-height: 1.55;
}

/* ── Expand Transition ────────────────────────────────────────────── */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}
.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 600px;
}
</style>
